// Patient Ledger Drawer — unified surface for the right-side detail pane.
//
// Hosts both eligibility content (Eligibility tab) and patient money
// content (Ledger tab) in a single drawer. The Eligibility tab is the
// successor to the standalone EligibilityDetailDrawer; the Ledger tab
// replaces PaymentRequestModal / RecordPaymentModal / WriteOffModal /
// CaptureChargesModal / LogCollectionForm.
//
// Wrapped by SlideSidePane (the production drawer chrome — resizable,
// click-outside-to-close, back-arrow + close X). Mounted by
// PatientLedgerDrawerHost from a single Redux slice — no modal stack
// involvement.
//
// "Log collection" inside the Eligibility tab flips this drawer to the
// Ledger tab + opens the settle sheet (method=external). No drawer hop,
// no nested side panes.

import { useEffect, useMemo, useState } from 'react';
import { displayGroupsFromPreview } from '@features/billing/components/charge-groups';
// Direct file import (no feature barrel) — same source the Eligibility tab uses.
import { useEligibilityBadgeState } from '@features/eligibility-checks/hooks/useEligibilityBadgeState';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import { useSubjectLanguageVariants } from '@features/user';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import SlideSidePane from '../../Views/Shared/SlideSidePane';

import { AddChargeSheet } from './components/AddChargeSheet';
import { EligibilityTabContent } from './components/EligibilityTabContent';
import { LedgerTabContent } from './components/LedgerTabContent';
import { SettleSheet } from './components/SettleSheet';
import { useLedgerData } from './hooks/useLedgerData';
import type { CopaySuggestion } from './utils/allocationEngine';
import type {
  LedgerDrawerProps,
  LedgerInitialAction,
  LedgerTab,
  SettleMethod,
} from './types';

interface DrawerProps extends LedgerDrawerProps {
  onClose: () => void;
}

const PatientLedgerDrawer = (props: DrawerProps) => {
  const { payment } = useEventTracking();
  const { singularCap } = useSubjectLanguageVariants();

  const isEncounter = !!props.encounterId;
  const [tab, setTab] = useState<LedgerTab>(
    props.initialTab ?? (isEncounter ? 'eligibility' : 'ledger'),
  );
  const [activeSheet, setActiveSheet] = useState<LedgerInitialAction | null>(
    props.initialAction ?? null,
  );

  // The drawer host keeps a single mounted instance and updates its props
  // when another eligibility/ledger trigger fires. Honor those destination
  // props after mount so in-drawer actions like "Invoice Client" can flip
  // from Eligibility to Ledger and open the requested sheet.
  useEffect(() => {
    setTab(props.initialTab ?? (isEncounter ? 'eligibility' : 'ledger'));
    setActiveSheet(props.initialAction ?? null);
  }, [
    isEncounter,
    props.encounterId,
    props.initialAction,
    props.initialTab,
    props.patientId,
  ]);

  const ledger = useLedgerData({
    patientId: props.patientId,
    encounterId: props.encounterId,
    chargeLineIds: props.chargeLineIds,
  });

  // Eligibility snapshot — same RTK-cached source the Eligibility tab reads.
  // Feeds the settle sheet's copay suggestion chip (amount + checked-at);
  // the production EligibilityCheckResponse doesn't expose a plan name.
  const { eligibilityData } = useEligibilityBadgeState({
    patientId: props.patientId,
    encounterId: props.encounterId ?? undefined,
  });
  const copaySuggestion = useMemo<CopaySuggestion | null>(() => {
    const financials: Record<string, unknown> | undefined =
      eligibilityData?.financials;
    const rawCopay = financials?.copayment_cents;
    if (typeof rawCopay !== 'number' || rawCopay <= 0) return null;
    return {
      amountCents: Math.round(rawCopay),
      checkedAt: eligibilityData?.last_checked_at ?? null,
    };
  }, [eligibilityData]);

  // Continuity of analytics with the modals being deprecated. Fires once
  // per open. The previous PaymentRequestModal/RecordPaymentModal/etc.
  // each emitted their own modalOpened — this is the consolidated equivalent.
  useEffect(() => {
    payment.modalOpened({ source: props.source ?? 'ledger_drawer' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const balance = ledger.balanceCents;
  const isSettled = balance === 0;
  const tone: 'green' | 'amber' = isSettled ? 'green' : 'amber';
  const toneColor = ELIG_TOKENS[tone];
  const previewLines = ledger.preview?.encounters?.flatMap(encounter => encounter.lines ?? []);
  const explicitChargeLineIds =
    props.chargeLineIds && props.chargeLineIds.length > 0
      ? new Set(props.chargeLineIds)
      : null;
  const invoiceableChargeLineIds =
    previewLines
      ?.filter(line => {
        if ((line.patient_due_cents ?? 0) <= 0 || line.already_invoiced) return false;
        return !explicitChargeLineIds || explicitChargeLineIds.has(line.charge_line_id);
      })
      .map(line => line.charge_line_id) ?? props.chargeLineIds;
  const invoiceableLineAmountCents = previewLines?.reduce((total, line) => {
    if ((line.patient_due_cents ?? 0) <= 0 || line.already_invoiced) return total;
    if (explicitChargeLineIds && !explicitChargeLineIds.has(line.charge_line_id)) {
      return total;
    }
    return total + (line.patient_due_cents ?? 0);
  }, 0);
  const invoiceableAmountCents =
    ledger.preview?.total_due_cents ?? invoiceableLineAmountCents;
  const selectableWriteOffChargeLineIds =
    previewLines
      ?.filter(line => {
        if ((line.patient_due_cents ?? 0) <= 0) return false;
        return !explicitChargeLineIds || explicitChargeLineIds.has(line.charge_line_id);
      })
      .map(line => line.charge_line_id) ?? props.chargeLineIds;
  const chargeGroups = displayGroupsFromPreview(ledger.preview);

  const patientName = props.patientName ?? ledger.patientName ?? '';
  const subtitle = isEncounter ? props.encounterLabel ?? 'Encounter' : 'All visits';
  const titleLine = patientName
    ? `${patientName}${subtitle ? ` · ${subtitle}` : ''}`
    : `${singularCap}${subtitle ? ` · ${subtitle}` : ''}`;

  const flipToLedgerSettleExternal = () => {
    setTab('ledger');
    setActiveSheet({ kind: 'settle', method: 'external' });
  };

  return (
    <SlideSidePane open onClose={props.onClose} title={titleLine}>
      {/* Strip CardContent's default padding so we control spacing fully,
          and provide a positioned column container so sheets can slide up
          from the bottom anchored to the visible drawer area. */}
      <Box
        sx={{
          m: '-16px',
          height: 'calc(100% + 32px)',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: ELIG_TOKENS.bg,
        }}
      >
        <LedgerHero
          balance={balance}
          isSettled={isSettled}
          toneColor={toneColor}
          loading={ledger.isLoading}
        />

        <TabStrip value={tab} onChange={setTab} />

        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
          {tab === 'eligibility' ? (
            <EligibilityTabContent
              patientId={props.patientId}
              encounterId={props.encounterId ?? undefined}
              patientName={patientName}
              source={props.source}
              initialForm={props.initialEligibilityForm}
              onLogCollection={flipToLedgerSettleExternal}
            />
          ) : (
            <LedgerTabContent
              patientId={props.patientId}
              entries={ledger.entries}
              encounter={ledger.encounter}
              preview={ledger.preview}
              openInvoices={ledger.openInvoices}
              staleInvoiceIds={ledger.staleInvoiceIds}
              isEncounterScope={isEncounter}
              showEncounterLabels={!isEncounter}
            />
          )}
        </Box>

        <DrawerFooter
          tab={tab}
          isSettled={isSettled}
          balance={balance}
          toneColor={toneColor}
          hasOpenInvoice={ledger.openInvoiceIds.length > 0}
          onSettle={(method) => setActiveSheet({ kind: 'settle', method })}
          onAddCharge={() => setActiveSheet({ kind: 'add_charge' })}
        />

        {activeSheet?.kind === 'settle' && (
          <SettleSheet
            // Re-seed the sheet when a new trigger lands a different preset
            // (e.g. toolbar copay route while the sheet is already open) —
            // destination-surface semantics over preserved half-typed state.
            key={`${activeSheet.method ?? 'default'}:${activeSheet.paymentIntent ?? 'general'}`}
            patientId={props.patientId}
            patientName={patientName}
            encounterId={props.encounterId}
            initialMethod={activeSheet.method}
            initialPaymentIntent={activeSheet.paymentIntent}
            // Invoice-like methods treat the amount as the NEW ad-hoc charge;
            // selected receipt lines add on top inside the sheet. Only an
            // explicit caller-provided amount should prefill it.
            initialAmountCents={
              activeSheet.method === 'invoice' || activeSheet.method === 'card_on_file'
                ? props.defaultAmountCents ?? 0
                : props.defaultAmountCents ?? (balance > 0 ? balance : 0)
            }
            preselectChargeLines={activeSheet.preselectLines ?? true}
            // BE-synthesized invoice reason from /preview — pre-fills the
            // SettleSheet's "Reason (shown on invoice)" field for the
            // invoice / card_on_file methods. User can still edit.
            suggestedReason={ledger.preview?.suggested_reason}
            invoiceChargeLineIds={invoiceableChargeLineIds}
            writableChargeLineIds={selectableWriteOffChargeLineIds}
            invoiceableAmountCents={invoiceableAmountCents}
            unallocatedCreditCents={ledger.preview?.unallocated_credit_cents ?? 0}
            chargeGroups={chargeGroups}
            reminderInvoiceId={ledger.openInvoiceIds[0]}
            copaySuggestion={copaySuggestion}
            source={props.source}
            onClose={() => setActiveSheet(null)}
          />
        )}
        {activeSheet?.kind === 'add_charge' && (
          <AddChargeSheet
            patientId={props.patientId}
            encounterId={props.encounterId}
            onClose={() => setActiveSheet(null)}
          />
        )}
      </Box>
    </SlideSidePane>
  );
};

// ─── Convergent balance signal ───────────────────────────────────────────────

const LedgerHero = ({
  balance,
  isSettled,
  toneColor,
  loading,
}: {
  balance: number;
  isSettled: boolean;
  toneColor: string;
  loading: boolean;
}) => {
  const label = isSettled ? 'Settled' : balance < 0 ? 'Credit' : 'Outstanding';
  return (
    <Box
      sx={{
        px: 3,
        py: 2,
        borderBottom: `1px solid ${ELIG_TOKENS.line}`,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        bgcolor: ELIG_TOKENS.card,
      }}
    >
      <Box
        sx={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          flexShrink: 0,
          ...(isSettled
            ? { bgcolor: toneColor }
            : { border: `2px solid ${toneColor}`, bgcolor: 'transparent' }),
        }}
      />
      <Typography
        variant="h5"
        sx={{ color: toneColor, fontWeight: 600, lineHeight: 1 }}
      >
        {label} {loading ? '—' : dollars(balance)}
      </Typography>
    </Box>
  );
};

// ─── Tab strip (encounter scope only) ────────────────────────────────────────

const TabStrip = ({
  value,
  onChange,
}: {
  value: LedgerTab;
  onChange: (v: LedgerTab) => void;
}) => (
  <Tabs
    value={value}
    onChange={(_, v) => onChange(v as LedgerTab)}
    sx={{
      px: 3,
      borderBottom: `1px solid ${ELIG_TOKENS.line}`,
      minHeight: 40,
      bgcolor: ELIG_TOKENS.card,
      '& .MuiTab-root': {
        minHeight: 40,
        textTransform: 'none',
        fontWeight: 600,
        color: ELIG_TOKENS.inkSoft,
        '&.Mui-selected': { color: ELIG_TOKENS.midnight },
      },
      '& .MuiTabs-indicator': { bgcolor: ELIG_TOKENS.midnight },
    }}
  >
    <Tab value="eligibility" label="Eligibility" />
    <Tab value="ledger" label="Ledger" />
  </Tabs>
);

// ─── Footer ──────────────────────────────────────────────────────────────────

const DrawerFooter = ({
  tab,
  isSettled,
  balance,
  toneColor,
  hasOpenInvoice,
  onSettle,
  onAddCharge,
}: {
  tab: LedgerTab;
  isSettled: boolean;
  balance: number;
  toneColor: string;
  hasOpenInvoice: boolean;
  onSettle: (method: SettleMethod) => void;
  onAddCharge: () => void;
}) => {
  // Eligibility tab keeps its own affordances (Verify benefits / Log
  // collection) inside the tab content. Footer only renders for Ledger tab.
  if (tab === 'eligibility') return null;

  return (
    <Box
      sx={{
        borderTop: `1px solid ${ELIG_TOKENS.line}`,
        bgcolor: ELIG_TOKENS.card,
        px: 3,
        py: 1.5,
        display: 'flex',
        gap: 1,
        alignItems: 'center',
      }}
    >
      <FooterTextButton onClick={onAddCharge}>+ Add charge</FooterTextButton>
      <Box sx={{ flex: 1 }} />
      {!isSettled && (
        <FooterPrimaryButton
          color={toneColor}
          onClick={() =>
            hasOpenInvoice ? onSettle('remind_invoice') : onSettle('cash')
          }
        >
          Settle {dollars(balance)}
        </FooterPrimaryButton>
      )}
    </Box>
  );
};

const FooterTextButton = ({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) => (
  <Box
    component="button"
    onClick={onClick}
    sx={{
      bgcolor: 'transparent',
      border: 0,
      cursor: 'pointer',
      color: ELIG_TOKENS.inkSoft,
      fontWeight: 600,
      fontSize: '0.875rem',
      textTransform: 'none',
      px: 1,
      py: 0.75,
      borderRadius: 1,
      '&:hover': { bgcolor: ELIG_TOKENS.line },
    }}
  >
    {children}
  </Box>
);

const FooterPrimaryButton = ({
  color,
  children,
  onClick,
}: {
  color: string;
  children: React.ReactNode;
  onClick: () => void;
}) => (
  <Box
    component="button"
    onClick={onClick}
    sx={{
      bgcolor: color,
      color: '#fff',
      border: 0,
      cursor: 'pointer',
      fontWeight: 600,
      fontSize: '0.875rem',
      textTransform: 'none',
      px: 2,
      py: 0.875,
      borderRadius: 1,
      '&:hover': { filter: 'brightness(0.9)' },
    }}
  >
    {children}
  </Box>
);

const dollars = (cents: number): string => {
  const abs = Math.abs(cents);
  const sign = cents < 0 ? '−' : '';
  if (abs % 100 === 0) return `${sign}$${abs / 100}`;
  return `${sign}$${(abs / 100).toFixed(2)}`;
};

export default PatientLedgerDrawer;
